import ActivateIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SwapHorizIcon from '@mui/icons-material/SwapHorizRounded';
import {
  Box,
  IconButton,
  Menu,
  MenuItem as MUIMenuItem,
  SvgIcon,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import type { MouseEvent } from 'react';
import { useRef, useState } from 'react';
import { useConfirmationDialog } from '@/shared/hooks';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Button } from '@/shared/ui';
import { selectRoles } from '../../model/rolesSlice';
import { resendInvite, updateUser } from '../../model/slice';
import type { User } from '../../model/types';
import { RoleChangePopover } from '../RoleChangePopover';
import { DeactivateUserIcon } from './DeactivateUserIcon';

const MenuItem = styled(MUIMenuItem)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
}));

export enum MenuButtonType {
  ICON = 'icon',
  BUTTON = 'button',
}

interface Props {
  type?: MenuButtonType;
  user: User;
}

export function UserActionsMenu({ type = MenuButtonType.ICON, user }: Props) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const roles = useAppSelector(selectRoles);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [rolePickerOpen, setRolePickerOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);

  const open = Boolean(anchorEl);

  const { openDialog, dialogElememnt } = useConfirmationDialog({
    slotProps: { dialog: { sx: { width: 370 } } },
    title: 'Deactivate',
    description: (
      <>
        If you deactivate the user, they will lose access to their account until
        it is reactivated.
        <Box component="p" sx={{ mt: 2, mb: 0, fontWeight: 500 }}>
          Are you sure you want to do this?
        </Box>
      </>
    ),
    acceptButtonColor: 'error',
    acceptButtonText: 'Deactivate',
    onAccept: async () => {
      try {
        await dispatch(
          updateUser({ user, update: { isActive: false } }),
        ).unwrap();
      } catch (error) {
        enqueueSnackbar('Unable to update the user.', {
          variant: 'error',
          autoHideDuration: 4000,
        });
        throw error;
      }
    },
  });

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    triggerRef.current = event.currentTarget;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleUserStatusChange = () => {
    if (user.isActive) {
      openDialog();
    } else {
      dispatch(updateUser({ user, update: { isActive: true } }));
    }
  };

  const handleOpenRolePicker = () => {
    setRolePickerOpen(true);
  };

  const handleApplyRole = async (roleId: string) => {
    try {
      await dispatch(updateUser({ user, update: { roleId } })).unwrap();
    } catch {
      enqueueSnackbar('Unable to update the user.', {
        variant: 'error',
        autoHideDuration: 4000,
      });
      throw new Error('Role update failed');
    }
  };

  const handleResendInvite = () => {
    dispatch(resendInvite(user.email))
      .unwrap()
      .then(() => {
        enqueueSnackbar('Invite resent successfully', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Unable to resend invite', { variant: 'error' });
      });
  };

  return (
    <>
      {type === MenuButtonType.ICON && (
        <IconButton sx={{ p: 0 }} size="small" onClick={handleClick}>
          <MoreVertIcon />
        </IconButton>
      )}
      {type === MenuButtonType.BUTTON && (
        <Button
          sx={{ py: 1.125 }}
          variant="outlined"
          color="secondary"
          startIcon={<MoreVertIcon />}
          onClick={handleClick}
        >
          More
        </Button>
      )}
      <Menu
        MenuListProps={{ sx: { p: 0, borderRadius: 2 } }}
        slotProps={{ paper: { sx: { borderRadius: 2 } } }}
        anchorEl={anchorEl}
        open={open}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={handleClose}
        onClick={handleClose}
      >
        {!user.lastActiveAt && (
          <MenuItem
            sx={{
              py: 0.75,
              color: 'text.primary',
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
            onClick={handleResendInvite}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <SvgIcon sx={{ color: '#7E828F', width: 20, height: 20 }}>
                <svg
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </SvgIcon>
              Resend invite
            </Box>
          </MenuItem>
        )}
        <MenuItem
          sx={{ py: 0.75, color: 'text.primary' }}
          onClick={handleOpenRolePicker}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <SwapHorizIcon sx={{ color: '#7E828F' }} />
            Change Role
          </Box>
        </MenuItem>
        <MenuItem
          sx={{ py: 0.75, color: 'text.primary' }}
          onClick={handleUserStatusChange}
        >
          {user.isActive ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <DeactivateUserIcon />
              Deactivate
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <ActivateIcon sx={{ color: '#7E828F' }} />
              Activate
            </Box>
          )}
        </MenuItem>
      </Menu>

      {dialogElememnt}

      <RoleChangePopover
        anchorEl={triggerRef.current}
        open={rolePickerOpen}
        onClose={() => setRolePickerOpen(false)}
        roles={roles ?? []}
        currentRoleId={user.roleId}
        onApply={handleApplyRole}
      />
    </>
  );
}
